/**
 * AgentOsDemoReviewReply.jsx
 *
 * Public live sandbox page — návštevník nemusí byť prihlásený. Vstupy:
 *  - hodnotenie 1-5 (star picker)
 *  - text recenzie
 *  - meno klienta (opt)
 *  - názov produktu (opt)
 *  - brand name + voice picker + jazyk
 *
 * Submit → POST /api/agent-os/demo/review-reply → ukáže AI reply
 * v cca 5-8s, generated badge a "remaining today" counter.
 *
 * Rate-limited: 3 generácie/IP/24h. Pri prekročení sa zobrazí
 * upgrade prompt do funnel B (eshop lead form).
 *
 * Toto je **najsilnejší conversion tool** Agent OS marketing layeru.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import {
  Sparkles, Star, ArrowRight, Loader2, Copy, CheckCircle, Lock, Bot,
} from "lucide-react";
import { AgentOsPixelTracker, LiveLeadCounter } from "../components/agent-os/AgentOsShared";

const VOICES = [
  { id: "warm",     name: "Vrúcny",   desc: "Empatický, ľudský, prirodzený" },
  { id: "formal",   name: "Formálny", desc: "Korporátny, profesionálny" },
  { id: "friendly", name: "Priateľský", desc: "Casual, tykanie, ľahké emoji" },
  { id: "premium",  name: "Premium",  desc: "Luxusný, low-key, kultivovaný" },
];

const LANGS = [
  { id: "sk", name: "Slovenčina" },
  { id: "cs", name: "Čeština" },
  { id: "en", name: "English" },
  { id: "de", name: "Deutsch" },
  { id: "pl", name: "Polski" },
  { id: "hu", name: "Magyar" },
];

const EXAMPLES = [
  { rating: 2, text: "Doručenie trvalo 8 dní, balíček prišiel poškodený. Čakal som lepšiu službu za túto cenu.", customer_name: "Karol", product_name: "Bluetooth slúchadlá" },
  { rating: 5, text: "Skvelý e-shop! Objednal som ráno, prišlo na druhý deň. Kvalita produktu top.", customer_name: "Janka", product_name: "Detský kočík" },
  { rating: 3, text: "Tovar OK, ale chcel som vedieť, či je možné vrátiť produkt po 14 dňoch ak nebudem spokojný?", customer_name: "Lukáš", product_name: "Bicykel horský" },
];

export default function AgentOsDemoReviewReply() {
  const [form, setForm] = useState({
    rating: 4,
    text: "",
    customer_name: "",
    product_name: "",
    brand_name: "",
    brand_voice: "warm",
    lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });
  const [copied, setCopied] = useState(false);

  const loadExample = (idx) => {
    setForm((f) => ({ ...f, ...EXAMPLES[idx] }));
    setResult(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) return;
    setStatus({ loading: true, error: null });
    setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/review-reply`, form);
      setResult(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof detail === "object" ? detail : { message: detail || "Skús to ešte raz." } });
      return;
    }
    setStatus({ loading: false, error: null });
  };

  const copyReply = async () => {
    if (!result?.reply) return;
    try {
      await navigator.clipboard.writeText(result.reply);
      setCopied(true);
    } catch {
      // Clipboard API denied (iframe, locked-down browsers). Fallback
      // to legacy execCommand which works in more contexts.
      try {
        const ta = document.createElement("textarea");
        ta.value = result.reply;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
      } catch { /* user can still select + copy manually */ }
    }
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="agent-os-demo-page">
      <AgentOsPixelTracker event="ViewContent" />

      {/* Top bar */}
      <div className="border-b border-emerald-900/30 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
              <Bot className="w-5 h-5 text-black" />
            </div>
            <span className="font-black tracking-tight">
              TopRankPulse <span className="text-emerald-400">· Agent OS · Demo</span>
            </span>
          </a>
          <a
            href="/agent-os/eshop#lead-form"
            data-testid="agent-os-demo-topbar-cta"
            className="px-4 py-2 bg-emerald-400 hover:bg-emerald-300 text-black text-xs font-black uppercase tracking-widest"
          >
            Spusti pre svoj e-shop →
          </a>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex mb-4">
            <LiveLeadCounter variant="eshop" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black mb-3" data-testid="agent-os-demo-h1">
            Vyskúšaj si <span className="text-emerald-400">Review Reply Agenta</span> naživo
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Vlož recenziu zákazníka → AI vygeneruje branded odpoveď v tvojom jazyku za 5 sekúnd.
            Žiadna registrácia. 3 pokusy zdarma.
          </p>
        </div>

        {/* Examples row */}
        <div className="mb-6 flex flex-wrap gap-2 justify-center">
          <span className="text-xs uppercase tracking-widest text-gray-500 self-center mr-2">Skús príklad:</span>
          {EXAMPLES.map((ex, i) => (
            <button
              key={i}
              type="button"
              onClick={() => loadExample(i)}
              data-testid={`agent-os-demo-example-${i}`}
              className="px-3 py-1.5 text-xs bg-black border border-gray-800 hover:border-emerald-700/60 text-gray-300 hover:text-emerald-300 transition-colors"
            >
              {ex.rating}★ {ex.product_name}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* INPUT */}
          <form onSubmit={submit} className="space-y-4 p-6 bg-black border border-gray-800">
            <h2 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-3">Recenzia zákazníka</h2>

            <div>
              <label className="text-xs text-gray-500 uppercase tracking-widest mb-2 block">Hodnotenie</label>
              <div className="flex gap-1" data-testid="agent-os-demo-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, rating: n })}
                    data-testid={`agent-os-demo-star-${n}`}
                    className="p-1"
                  >
                    <Star className={`w-7 h-7 ${form.rating >= n ? "fill-emerald-400 text-emerald-400" : "text-gray-700"}`} />
                  </button>
                ))}
              </div>
            </div>

            <textarea
              required
              rows={4}
              placeholder={'Text recenzie (napr. „Doručenie trvalo 8 dní…")'}
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              data-testid="agent-os-demo-text"
              className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600"
            />

            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Meno zákazníka"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                data-testid="agent-os-demo-customer"
                className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600"
              />
              <input
                type="text"
                placeholder="Produkt"
                value={form.product_name}
                onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                data-testid="agent-os-demo-product"
                className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600"
              />
            </div>

            <hr className="border-gray-800" />

            <h3 className="text-sm font-black uppercase tracking-widest text-gray-400">Tvoj brand</h3>
            <input
              type="text"
                placeholder={'Názov e-shopu (napr. „Krásne Boty SK")'}
              value={form.brand_name}
              onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
              data-testid="agent-os-demo-brand"
              className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600"
            />

            <div>
              <label className="text-xs text-gray-500 uppercase tracking-widest mb-2 block">Tón odpovede</label>
              <div className="grid grid-cols-2 gap-2" data-testid="agent-os-demo-voices">
                {VOICES.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setForm({ ...form, brand_voice: v.id })}
                    data-testid={`agent-os-demo-voice-${v.id}`}
                    className={`p-2 text-left text-xs border transition-colors ${
                      form.brand_voice === v.id
                        ? "bg-emerald-950/40 border-emerald-500 text-white"
                        : "bg-[#0a0a0a] border-gray-800 text-gray-400 hover:border-emerald-700/40"
                    }`}
                  >
                    <div className="font-bold">{v.name}</div>
                    <div className="text-[10px] opacity-80">{v.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 uppercase tracking-widest mb-2 block">Jazyk odpovede</label>
              <select
                value={form.lang}
                onChange={(e) => setForm({ ...form, lang: e.target.value })}
                data-testid="agent-os-demo-lang"
                className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white"
              >
                {LANGS.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>

            <button
              type="submit"
              disabled={status.loading || !form.text.trim()}
              data-testid="agent-os-demo-submit"
              className="w-full px-5 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2"
            >
              {status.loading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Generujem…</>
                : <>Vygeneruj odpoveď <Sparkles className="w-4 h-4" /></>}
            </button>
          </form>

          {/* OUTPUT */}
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-br from-emerald-950/30 to-black border-2 border-emerald-700/40 min-h-[300px]">
              <h2 className="text-sm font-black uppercase tracking-widest text-emerald-300 mb-3">
                AI Reply
              </h2>

              {!result && !status.loading && !status.error && (
                <div className="text-center py-12 text-gray-500 text-sm" data-testid="agent-os-demo-empty">
                  <Sparkles className="w-8 h-8 mx-auto mb-3 text-gray-700" />
                  Vyplň formulár a klikni „Vygeneruj odpoveď"
                </div>
              )}

              {status.loading && (
                <div className="text-center py-12 text-emerald-300 text-sm" data-testid="agent-os-demo-loading">
                  <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" />
                  AI premýšľa nad odpoveďou…
                </div>
              )}

              {status.error && (
                <div className="p-4 bg-rose-950/30 border border-rose-700/40 text-rose-200 text-sm" data-testid="agent-os-demo-error">
                  <div className="font-bold mb-1">{status.error?.message || "Niečo zlyhalo"}</div>
                  {status.error?.cta_url && (
                    <a
                      href={status.error.cta_url}
                      className="inline-flex items-center gap-1 mt-2 text-emerald-300 underline text-xs font-bold"
                    >
                      Registruj sa zadarmo <ArrowRight className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}

              {result && (
                <div data-testid="agent-os-demo-result">
                  <div className="p-4 bg-black/50 border border-emerald-900/40 text-white text-base leading-relaxed mb-4">
                    {result.reply}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4 text-[10px] uppercase tracking-widest">
                    <span className="px-2 py-1 bg-emerald-900/40 text-emerald-300 font-bold">{result.intent}</span>
                    <span className="px-2 py-1 bg-gray-900 text-gray-400">{result.latency_ms} ms</span>
                    {result.used_fallback && (
                      <span className="px-2 py-1 bg-amber-900/30 text-amber-300 font-bold">Fallback</span>
                    )}
                    <span className="px-2 py-1 bg-gray-900 text-gray-400">
                      Zostáva dnes: {result.remaining_today}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={copyReply}
                      data-testid="agent-os-demo-copy"
                      className="flex-1 px-4 py-2 border border-gray-700 hover:border-emerald-400 text-xs font-bold uppercase tracking-widest text-white flex items-center justify-center gap-2"
                    >
                      {copied ? <><CheckCircle className="w-3 h-3 text-emerald-400" /> Skopírované</> : <><Copy className="w-3 h-3" /> Kopírovať</>}
                    </button>
                    <button
                      onClick={() => {
                        const caption = `🤖 Pozri ako AI odpovedá môjmu zákazníkovi za 2 sekundy:\n\n"${result.reply}"\n\n→ toprankpulse.com/agent-os/demo`;
                        navigator.clipboard?.writeText(caption).then(() => {
                          alert("Caption skopírovaná! Vlož na FB/IG/X.");
                        }).catch(() => {});
                      }}
                      data-testid="agent-os-demo-share"
                      className="flex-1 px-4 py-2 border border-gray-700 hover:border-emerald-400 text-xs font-bold uppercase tracking-widest text-white flex items-center justify-center gap-2"
                      title="Zdieľaj svoju AI odpoveď"
                    >
                      <Sparkles className="w-3 h-3" /> Zdieľaj
                    </button>
                    <a
                      href="/agent-os/onboard"
                      data-testid="agent-os-demo-cta"
                      onClick={() => window.fbq && window.fbq("track", "InitiateCheckout")}
                      className="flex-1 px-4 py-2 bg-emerald-400 hover:bg-emerald-300 text-black text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2"
                    >
                      Aktivovať <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 bg-black border border-gray-800 text-xs text-gray-500 flex gap-3 items-start">
              <Lock className="w-4 h-4 shrink-0 mt-0.5 text-gray-600" />
              <div>
                Demo má limit 3 generácie / IP / 24h.
                Žiadne uloženie vstupných dát mimo telemetry pre training.
                <a href="/privacy" className="text-emerald-400 underline ml-1">Privacy →</a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-16 p-8 bg-gradient-to-r from-emerald-950/40 to-black border border-emerald-700/40 text-center">
          <h2 className="text-2xl sm:text-3xl font-black mb-3">
            Páči sa ti to? Aktivuj <span className="text-emerald-400">naživo</span> pre svoj e-shop.
          </h2>
          <p className="text-sm text-gray-400 mb-6 max-w-xl mx-auto">
            14 dní zadarmo bez karty. Automatické odpovede 24/7. 26 jazykov. €99/mes.
          </p>
          <a
            href="/agent-os/onboard"
            data-testid="agent-os-demo-bottom-cta"
            className="inline-flex items-center gap-2 px-7 py-3 bg-emerald-400 hover:bg-emerald-300 text-black font-black uppercase tracking-widest text-sm"
          >
            Spusti onboarding (60s) <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </main>

      <footer className="border-t border-gray-900 py-8 text-center text-xs text-gray-600">
        © 2026 TopRankPulse · Agent OS
      </footer>
    </div>
  );
}
